import React, { useCallback, useState } from "react";
import styled from "styled-components";
import Skeleton from "react-loading-skeleton";
import useFetch from "use-http";

import { Metrics } from "~/components/analytics/shared/type";
import { DateFilter } from "~/components/analytics/shared/DateFilters";
import InteractiveChart from "~/components/analytics/shared/InteractiveChart";
import Dialog from "~/components/shared/Dialog";
import Tooltip from "~/components/analytics/shared/Tooltips/BasicTooltip";
import MiniChart from "~/components/analytics/shared/MetricChartCards/ChartCard/MiniChart";

type ChartCardProps = {
  chartConfig: Metrics;
  heading?: string;
  chartDataUrl: string;
  chartMetricUrl: string;
  period: DateFilter;
};

const StyledCard = styled.span`
  display: flex;
  justify-content: space-between;
  height: 100%;
  min-width: 0;
  box-shadow:
    0px 0px 0px 1px rgba(0, 0, 0, 0.09),
    0px 1px 0px rgba(0, 0, 0, 0.09),
    0px 2px 2px rgba(0, 0, 0, 0.04),
    0px 3px 3px rgba(0, 0, 0, 0.02),
    0px 4px 4px rgba(0, 0, 0, 0.01);

  @media (min-width: 769px) {
    align-items: stretch;
    flex: 1 1 0;
    flex-direction: column;
  }

  &:hover {
    box-shadow:
      0px 0px 0px 1px rgba(0, 0, 0, 0.09),
      0px 1px 0px rgba(0, 0, 0, 0.09),
      0px 2px 2px rgba(0, 0, 0, 0.04),
      0px 10px 14px rgba(0, 0, 0, 0.02),
      0px 6px 7px -2px rgba(0, 0, 0, 0.08);
  }
`;

const Wrapper = ({ onClick, children }) => {
  const tag = onClick ? "button" : "div";
  const classNames = [
    "no-title-tooltip",
    "flex",
    "h-full",
    "w-full",
    "flex-col",
    "p-5",
    "text-left",
    "font-sans",
    "font-normal",
  ];
  if (onClick) {
    classNames.push("unstyled-button");
  }
  return React.createElement(
    tag,
    {
      ...(onClick && {
        onClick,
        "aria-label": "Open chart",
      }),
      className: classNames.join(" "),
    },
    children,
  );
};

// ChartCard renders a card/button which displays a metric value and a mini chart.
// When clicked, it opens a dialog with an interactive chart. We use the `chartMetricUrl` endpoint
// here to fetch the metric for the card, and the `chartDataUrl` to pass through into both the mini chart
// and interactive chart to fetch the data for the charts.
const ChartCard = ({
  chartConfig,
  heading,
  chartDataUrl,
  chartMetricUrl,
  period,
}: ChartCardProps) => {
  const { metric, label, description, chartType } = chartConfig;
  const url = new URL(chartMetricUrl);
  url.searchParams.set("metric", metric);
  // We still need to support the period filter changing by react state,
  // this is used in the InteractiveChart dialog and in the suite summary
  url.searchParams.set("period", period);

  const { loading, error, data } = useFetch(url.href, [period, metric]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const handleModalClose = useCallback(() => setIsModalOpen(false), []);
  const handleCardOnClick = useCallback(() => setIsModalOpen(true), []);

  return (
    <StyledCard
      id={`chart-card-${metric}`}
      className="relative cursor-pointer rounded bg-white text-left"
    >
      {description && (
        <div className="absolute right-5 top-4 z-[2]" style={{ top: "1.3em" }}>
          <Tooltip name={`${label} calculation`}>
            <>
              <p className="m-0 font-semibold">{label}</p>
              <p className="m-0 mt-1" dangerouslySetInnerHTML={{ __html: description }} />
            </>
          </Tooltip>
        </div>
      )}

      <Wrapper onClick={chartType ? handleCardOnClick : undefined}>
        <div className="mb-1 pr-6 text-sm">{label}</div>
        <div className="flex h-full w-full flex-col justify-between overflow-hidden">
          <div className="mb-5 leading-6">
            {error && <div>There was an error loading {label.toLowerCase()} data</div>}
            {loading && <Skeleton containerTestId="loading-skeleton" />}
            {!loading && !error && (
              <div className="m-0 text-2xl font-semibold">{data !== null ? data : "-"}</div>
            )}
          </div>

          {chartType && (
            <div>
              <MiniChart
                metric={metric}
                chartDataUrl={chartDataUrl}
                chartType={chartType}
                period={period}
              />
            </div>
          )}
        </div>
      </Wrapper>

      {chartType && (
        <Dialog isOpen={isModalOpen} onRequestClose={handleModalClose} width="80vw">
          <div className="p-5">
            <InteractiveChart
              label={label}
              metric={metric}
              chartDataUrl={chartDataUrl}
              heading={heading}
              description={description}
              chartType={chartType}
              period={period}
            />
          </div>
        </Dialog>
      )}
    </StyledCard>
  );
};

export default ChartCard;
